import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../redux";
import { useSelector } from "react-redux";
import {
  getSurveyFormField,
  saveSurvey,
} from "../../redux/reducer/surveyReducer";
import ErrorAlertMessage from "../../components/alertMessageModal/ErrorAlertMessage";
import { getErrorMessage, getTranslatedLabel } from "../../utils/helpers";
import HeaderModal from "../../components/headerModal";
import HeaderSection from "../../components/header-section";
import DynamicForm from "../../components/common/forms/DynamicForm";
import ButtonSubmit from "../../components/common/buttonSubmit";

// Component starts
const SurveyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * Get citizen id from url.
   */
  const { id } = useParams();
  const location = useLocation();
  const { urlHistory, citizenDetail } = location.state || {};
  const { citizenId, name, surname, fiscalcode } = citizenDetail;

  /**
   * redux hooks.
   */
  const { eventId } = useSelector((state: any) => state.login);
  const { surveys } = useSelector((state: any) => state);
  const { apiError } = surveys;

  /**
   * Handling date range selection allows in dynamic forms.
   */
  const [dates, setDates] = useState<any>(null);

  /**
   * Formates header title with combination of fiscal code, name and surname.
   */
  const headerTitle =
    fiscalcode.concat(name || surname ? " - " : "") + [name, surname].join(" ");

  /**
   * react-hook-form hooks.
   */
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm();

  /**
   * state variables.
   */
  const [formFields, setFormFields] = useState<any>([]);
  const [fieldLoading, setFieldLoading] = useState(false);
  const [notificationType, setNotificationType] = useState("");

  /**
   * Get survey form fields.
   */
  useEffect(() => {
    if (id) {
      setFieldLoading(true);
      fetchFormField(id);
    }
  }, [id]);

  const fetchFormField = async (id: any) => {
    const result = await dispatch(getSurveyFormField(id));
    if (result) {
      setFormFields(result);
      setFieldLoading(false);
    }
  };

  /**
   * submit form.
   */
  const onSubmit = async (data: any) => {
    try {
      const newSurveyId = await dispatch(
        saveSurvey(citizenId, eventId, id, data)
      );
      if (newSurveyId) {
        redirectURL("success");
      } else {
        setNotificationType("error");
      }
    } catch (error) {
      console.log("add new survey:", error);
    }
  };

  /**
   * redirect to previous page
   */
  const redirectURL = (status: string) => {
    if (status) {
      navigate(`${urlHistory}?notification=${status}&type=survey`);
    } else {
      navigate(urlHistory);
    }
  };

  /**
   * update notificationType value
   */
  const handleClose = () => {
    setNotificationType("");
  };

  /**
   * get max date.
   */
  const getMaxDate = (name: string) => {
    return name === "compilation_date" && dates?.data_somministrazione
      ? new Date(dates.data_somministrazione)
      : null;
  };

  /**
   * get min date.
   */
  const getMinDate = (name: string) => {
    return name === "data_somministrazione" && dates?.compilation_date
      ? new Date(dates.compilation_date)
      : null;
  };

  const handleChange = (date: any, name: string) => {
    setDates({ ...dates, [name]: date });
  };

  return (
    <>
      {notificationType === "error" && (
        <ErrorAlertMessage
          icon="info"
          handleClose={handleClose}
          message={apiError ? getErrorMessage(apiError) : t("add-survey-error")}
        />
      )}

      <HeaderModal
        title={headerTitle}
        textButtonRight={t("new-group-link-modal.error-back")}
        handleClick={() => redirectURL("")}
      />
      <div className="page-title">
        <HeaderSection
          visibleButton={false}
          titleSection={
            formFields?.title ? getTranslatedLabel(formFields.title) : ""
          }
        />
      </div>

      <div className="container-box mb-3 d-flex justify-content-center">
        <div className="survey-wrapper-box col-md-6">
          <div className="col-md-11 mt-4">
            <form>
              {!fieldLoading && (
                <>
                  {formFields?.list?.length > 0 &&
                    formFields.list.map((formField: any) => (
                      <DynamicForm
                        key={formField.name}
                        formField={formField}
                        {...{ register, control, errors, setValue }}
                        //  extra props to manage date selection for fields: compilation_date, data_somministrazione
                        extraProps={{
                          maxDate: getMaxDate(formField.name),
                          minDate: getMinDate(formField.name),
                          onChangeDate: handleChange,
                        }}
                      />
                    ))}
                  <div className="d-flex pt-4 pb-4 justify-content-between">
                    <div className="w-100 d-flex justify-content-end">
                      <ButtonSubmit
                        className="btn-width"
                        textButton={t("save")}
                        spinner={false}
                        handleChange={handleSubmit(onSubmit)}
                      />
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SurveyForm;
